/* Order Register "More" menu — keeps daily + your most-used toolbar buttons visible, folds the rest. */
import { useCallback, useEffect, useMemo, useState } from "react";
import { MoreHorizontal, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const KEEP_EXACT = ["or-upload", "or-order-sheet-btn"];
const KEEP = ["or-search", "or-mode", "or-add", "or-teamview", "or-today-invoices", "or-tools", "or-cols",
  "or-menu", "or-refresh", "or-card-", "or-more", "or-photo-requests", "or-correction"];
const TOP_N = 8;
const LS = "or_tb_usage";
const readUsage = () => { try { return JSON.parse(localStorage.getItem(LS) || "{}"); } catch { return {}; } };
const kept = (id) => KEEP_EXACT.includes(id) || KEEP.some(k => id.startsWith(k));
// Buttons showing a live alert count (red / pink badge with a non-zero number) stay on the toolbar.
const hasAlert = (b) => [...b.querySelectorAll('[class*="bg-red"],[class*="bg-rose"],[class*="bg-fuchsia"]')]
  .some(el => /[1-9]/.test(el.textContent || ""));

export const OrToolbarMore = ({ containerId = "or-toolbar-sticky" }) => {
  const [cands, setCands] = useState([]);
  const [usage, setUsage] = useState(readUsage);
  const [revealed, setRevealed] = useState(() => new Set());
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const scan = useCallback(() => {
    const root = document.querySelector(`[data-testid="${containerId}"]`);
    if (!root) return;
    const seen = new Map();
    root.querySelectorAll("button[data-testid]").forEach(b => {
      const id = b.getAttribute("data-testid");
      if (kept(id) || seen.has(id) || b.closest("[role=dialog],[role=menu],[data-radix-popper-content-wrapper]")) return;
      const txt = (b.textContent || "").replace(/\s+/g, " ").trim();
      if (!txt || /^clear filters$/i.test(txt) || hasAlert(b)) return;
      seen.set(id, txt.slice(0, 40));
    });
    const next = [...seen].map(([id, label]) => ({ id, label }));
    setCands(prev => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
  }, [containerId]);

  useEffect(() => {
    scan();
    const t = setInterval(scan, 3000);
    const root = document.querySelector(`[data-testid="${containerId}"]`);
    const onClick = (e) => {
      const b = e.target.closest?.("button[data-testid]");
      if (!b || !root?.contains(b)) return;
      const id = b.getAttribute("data-testid");
      const u = readUsage(); u[id] = (u[id] || 0) + 1;
      localStorage.setItem(LS, JSON.stringify(u)); setUsage(u);
    };
    root?.addEventListener("click", onClick, true);
    return () => { clearInterval(t); root?.removeEventListener("click", onClick, true); };
  }, [scan, containerId]);

  const top = useMemo(() => new Set(Object.entries(usage).filter(([id, n]) => n >= 2 && !kept(id))
    .sort((a, b) => b[1] - a[1]).slice(0, TOP_N).map(([id]) => id)), [usage]);
  const folded = cands.filter(c => !top.has(c.id) && !revealed.has(c.id));
  const listed = folded.filter(c => !q.trim() || c.label.toLowerCase().includes(q.trim().toLowerCase()))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
  const css = folded.map(c => `[data-testid="${containerId}"] button[data-testid="${c.id}"]{display:none!important}`).join("\n");
  const pick = (id) => {
    setRevealed(s => new Set(s).add(id)); setOpen(false); setQ("");
    setTimeout(() => document.querySelector(`[data-testid="${containerId}"] button[data-testid="${id}"]`)?.click(), 60);
  };
  if (!folded.length) return null;
  return (
    <>
      <style>{css}</style>
      <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQ(""); }}>
        <PopoverTrigger asChild>
          <button type="button" className="inline-flex h-8 items-center gap-1 rounded-md border border-slate-300 bg-white px-2.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
            data-testid="or-more-btn"><MoreHorizontal className="h-3.5 w-3.5" /> More <span className="rounded-full bg-slate-100 px-1.5 text-[10px]">{folded.length}</span></button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-64 p-1" data-testid="or-more-menu">
          <div className="relative p-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search buttons…"
              onKeyDown={(e) => { if (e.key === "Enter" && listed[0]) pick(listed[0].id); }}
              className="h-8 w-full rounded-md border border-slate-300 pl-7 pr-2 text-xs outline-none focus:border-indigo-400" data-testid="or-more-search" />
          </div>
          <p className="px-2 pb-1 text-[10px] text-slate-400">A–Z · buttons you use most stay on the toolbar automatically.</p>
          <div className="max-h-72 overflow-y-auto">
          {!listed.length && <p className="px-2 py-3 text-center text-xs text-slate-400" data-testid="or-more-empty">No button matches “{q}”</p>}
          {listed.map(c => (
            <button key={c.id} type="button" onClick={() => pick(c.id)}
              className="block w-full truncate rounded px-2 py-1.5 text-left text-xs text-slate-700 hover:bg-slate-100"
              data-testid={`or-more-item-${c.id}`}>{c.label}</button>))}
          </div>
        </PopoverContent>
      </Popover>
    </>
  );
};
